import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert, Button, Chip, CircularProgress, List, ListItem, ListItemText, Stack, Typography,
} from '@mui/material';
import StyleConstants from '../../../core/styles.constants';
import type { AutomationFailure } from '../services/floorplan-automation.service';
import {
  levelOf, type FloorPlanIssue, type FloorPlanValidation, type IssueLevel,
} from '../models/floorplan-automation';

interface Props {
  readonly validation?: FloorPlanValidation;
  readonly busy: boolean;
  readonly failed?: AutomationFailure;
  readonly onRun: () => void;
  /** True while the editor holds unsaved edits, which the report does not know about. */
  readonly dirty: boolean;
}

const LEVEL_COLOUR: Readonly<Record<IssueLevel, 'error' | 'warning' | 'info'>> = {
  Error: 'error',
  Warning: 'warning',
  Info: 'info',
};

/**
 * The pre-flight check on the plan as it is stored.
 *
 * Reports rather than fixes. Most of what it finds is not a fault at all — room zones sharing
 * an id is how one room is spread across separate areas, an empty room is a meeting room —
 * and the two things that really break a plan, a desk with no Workplaces row behind it and a
 * feature with empty geometry, are worth finding among them. A panel that called everything
 * an error would train people to close it.
 *
 * Deliberately checks the stored plan, not the document on screen. The questions it answers
 * cannot be asked of the GeoJSON alone, and unsaved edits are exactly what it cannot see —
 * which is why it says so when there are any.
 */
const ValidatePanel = ({ validation, busy, failed, onRun, dirty }: Props): React.JSX.Element => {
  const { t } = useTranslation();

  const describe = (issue: FloorPlanIssue): string => String(t(`floorplan-validation-${issue.code}`, {
    count: issue.count,
    layer: issue.layer,
  }));

  return (
    <Stack spacing={1} sx={{ p: 1, minHeight: 0 }}>
      <Button size="small" variant="contained" onClick={onRun} disabled={busy}>
        {busy ? <CircularProgress size={16} /> : t('floorplan-editor-validate-run')}
      </Button>

      {dirty && (
        <Alert severity="info" sx={{ py: 0 }}>
          {t('floorplan-editor-validate-unsaved')}
        </Alert>
      )}

      {failed && (
        <Alert severity={failed === 'forbidden' ? 'warning' : 'error'} sx={{ py: 0 }}>
          {t(failed === 'forbidden' ? 'floorplan-editor-rooms-forbidden' : 'floorplan-editor-automation-failed')}
        </Alert>
      )}

      {validation && validation.issues.length === 0 && (
        <Alert severity="success" sx={{ py: 0 }}>{t('floorplan-editor-validate-clean')}</Alert>
      )}

      {validation && validation.issues.length > 0 && (
        <>
          <Stack direction="row" spacing={1}>
            <Chip
              size="small"
              color="error"
              variant="outlined"
              label={t('floorplan-editor-validate-errors', { count: validation.errors })}
            />
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              label={t('floorplan-editor-validate-warnings', { count: validation.warnings })}
            />
          </Stack>

          <List dense sx={{ overflowY: 'auto' }}>
            {validation.issues.map((issue) => (
              <ListItem key={`${issue.layer}-${issue.code}`} sx={{ px: 0, alignItems: 'flex-start' }}>
                <ListItemText
                  primaryTypographyProps={{ variant: 'body2' }}
                  primary={describe(issue)}
                  secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
                  // The layer and one affected feature, so a count of 40 is something the
                  // admin can start looking at rather than a number to worry about.
                  secondary={issue.example ? `${issue.layer} · ${issue.example}` : issue.layer}
                />
                <Chip
                  size="small"
                  color={LEVEL_COLOUR[levelOf(issue.level)]}
                  variant="outlined"
                  label={t(`floorplan-editor-validate-level-${levelOf(issue.level).toLowerCase()}`)}
                />
              </ListItem>
            ))}
          </List>
        </>
      )}

      {!validation && !busy && !failed && (
        <Typography variant="caption" color={StyleConstants.GREY_COLOR}>
          {t('floorplan-editor-validate-hint')}
        </Typography>
      )}
    </Stack>
  );
};

export default ValidatePanel;
